/**
 * useRecipientPdfExport - Hook for exporting recipient details to PDF
 *
 * Handles:
 * - Data collection from existing queries
 * - Chart image capture using SVG-to-PNG conversion (non-blocking)
 * - PDF generation using @react-pdf/renderer (dynamically imported)
 * - File download
 *
 * Uses dynamic imports to keep the PDF libraries out of the main bundle (bundle-dynamic-imports).
 * Legends are rendered using @react-pdf components with the actual data (no html2canvas).
 */
import { useCallback, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { toast } from 'sonner'

import type {
  AwardTypeData,
  CertificationBadge,
  ChartImages,
  GeoStateData,
  IndustryCodeData,
  RecipientPdfData,
} from '@/components/usaspending/RecipientDetail/export/types'

import type {
  CategoryBreakdown,
  ExecutiveOfficer,
  GeographicDistribution,
  Person,
  RecipientAwardSummary,
  RecipientCertifications,
  RecipientInsight,
  RecipientProfile,
  TopCategory,
} from '@/types/usaspending'
import type { RecipientDashboard } from '@/types/usaspending/analytics'

import { usaspendingKeys } from './queryKeys'
import { recipientAnalyticsKeys } from './useRecipientAnalytics'

/**
 * Chart ref registry for capturing chart images
 * Note: topAgencies is rendered as a table, not captured as an image
 */
export interface ChartRefRegistry {
  spendingTrend?: HTMLElement | null
  awardMix?: HTMLElement | null
  geography?: HTMLElement | null
}

/**
 * Certification badge configuration (matching RecipientHeader)
 */
const CERT_BADGE_CONFIG: Array<{
  key: string
  label: string
  shortLabel: string
}> = [
  { key: 'is8a', label: '8(a) Program', shortLabel: '8(a)' },
  { key: 'hubzone', label: 'HUBZone', shortLabel: 'HUBZone' },
  { key: 'sdvosb', label: 'Service-Disabled Veteran', shortLabel: 'SDVOSB' },
  { key: 'womanOwned', label: 'Woman-Owned', shortLabel: 'WOSB' },
  { key: 'veteranOwned', label: 'Veteran-Owned', shortLabel: 'VOSB' },
  { key: 'smallBusiness', label: 'Small Business', shortLabel: 'SB' },
  { key: 'sdb', label: 'Small Disadvantaged', shortLabel: 'SDB' },
  { key: 'minorityOwned', label: 'Minority-Owned', shortLabel: 'MBE' },
  {
    key: 'alaskaNative',
    label: 'Alaska Native Corporation',
    shortLabel: 'ANC',
  },
  { key: 'nativeAmerican', label: 'Native American Owned', shortLabel: 'NAO' },
  { key: 'nonprofit', label: 'Nonprofit', shortLabel: 'NPO' },
  { key: 'educational', label: 'Educational', shortLabel: 'EDU' },
]

/**
 * Color palette for award type categories (matching AwardMixDonut component)
 */
const AWARD_TYPE_COLORS: Record<string, string> = {
  contract: '#3b82f6', // Blue (matches AwardMixDonut)
  idv: '#6366f1', // Indigo
  grant: '#10b981', // Emerald
  loans: '#f59e0b', // Amber
  'direct payment': '#ec4899', // Pink
  other: '#6b7280', // Gray
}

/**
 * Sanitize filename for safe download
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80)
}

/**
 * Format date for filename
 */
function formatDateForFilename(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/**
 * Yield to browser to keep UI responsive during operations.
 */
function yieldToBrowser(ms = 0): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Check if a CSS value contains unsupported color functions (oklch, lab, etc.)
 */
function hasUnsupportedColorFunction(value: string): boolean {
  return (
    value.includes('color(') ||
    value.includes('oklch(') ||
    value.includes('oklab(') ||
    value.includes('lab(') ||
    value.includes('lch(')
  )
}

/**
 * Inline computed styles onto SVG elements for standalone rendering.
 * This ensures the SVG looks correct when extracted from the DOM.
 */
function inlineSvgStyles(svg: SVGElement): void {
  const elements = svg.querySelectorAll('*')

  // Key SVG style properties to inline
  const svgStyleProps = [
    'fill',
    'stroke',
    'stroke-width',
    'stroke-dasharray',
    'stroke-linecap',
    'stroke-linejoin',
    'opacity',
    'fill-opacity',
    'stroke-opacity',
    'font-family',
    'font-size',
    'font-weight',
    'text-anchor',
    'dominant-baseline',
  ]

  elements.forEach((el) => {
    if (el instanceof SVGElement || el instanceof Element) {
      const computed = window.getComputedStyle(el)
      svgStyleProps.forEach((prop) => {
        const value = computed.getPropertyValue(prop)
        if (value && value !== 'none' && value !== '') {
          // Handle modern color functions by replacing with fallbacks
          let safeValue = value
          if (hasUnsupportedColorFunction(value)) {
            if (prop === 'fill') {
              safeValue = '#3b82f6' // Blue for fills
            } else if (prop === 'stroke') {
              safeValue = '#64748b' // Gray for strokes
            }
          }
          el.setAttribute(prop, safeValue)
        }
      })
    }
  })
}

/**
 * Convert SVG element to PNG data URL using browser's native canvas.
 * This is fast and non-blocking.
 */
async function svgToPng(
  svgElement: SVGElement,
  scale = 2,
): Promise<string | undefined> {
  // Clone and prepare the SVG
  const clonedSvg = svgElement.cloneNode(true) as SVGElement
  const rect = svgElement.getBoundingClientRect()
  const width = Math.round(rect.width)
  const height = Math.round(rect.height)

  // Set explicit dimensions and namespace
  clonedSvg.setAttribute('width', String(width))
  clonedSvg.setAttribute('height', String(height))
  clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

  // Inline computed styles
  inlineSvgStyles(clonedSvg)

  // Serialize SVG to data URL
  const svgString = new XMLSerializer().serializeToString(clonedSvg)
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`

  // Create image and load the SVG
  const img = new Image()
  img.src = svgDataUrl

  // Wait for image to load (non-blocking)
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('Failed to load SVG as image'))
  })

  // Render to canvas at higher resolution for PDF quality
  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to get canvas context')
  }

  // White background for PDF
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Scale and draw
  ctx.scale(scale, scale)
  ctx.drawImage(img, 0, 0, width, height)

  return canvas.toDataURL('image/png')
}

/**
 * Capture a chart element as PNG using SVG-to-canvas conversion.
 * This is fast and non-blocking. Legends are rendered separately using PDF components.
 */
async function captureChartImage(
  element: HTMLElement | null | undefined,
): Promise<string | undefined> {
  if (!element) {
    return undefined
  }

  // Prefer an explicit export target when present (used by richer map UIs
  // that render a hidden SVG fallback for PDF capture).
  const svgElement =
    element.querySelector<SVGElement>('[data-export-chart] svg') ??
    // Fall back to the first SVG element (Recharts and most chart libraries render as SVG)
    element.querySelector<SVGElement>('svg')

  if (!svgElement) {
    return undefined
  }

  try {
    return await svgToPng(svgElement)
  } catch {
    return undefined
  }
}

/**
 * Extract certifications from profile data
 */
function extractCertifications(
  certifications: RecipientCertifications | undefined,
): CertificationBadge[] {
  if (!certifications) return []
  return CERT_BADGE_CONFIG.filter(
    (config) => certifications[config.key as keyof RecipientCertifications],
  )
}

/**
 * Convert TopCategory to IndustryCodeData
 */
function toIndustryCodeData(data: TopCategory[]): IndustryCodeData[] {
  return data.map((item) => ({
    code: item.code || item.name.slice(0, 10),
    description: item.name,
    amount: item.amount,
    percentage: item.percentage,
  }))
}

/**
 * Convert Person[] to ExecutiveOfficer[] for PDF export
 */
function toExecutiveOfficers(
  persons: Person[] | undefined,
): ExecutiveOfficer[] {
  if (!persons) return []
  return persons.map((person) => ({
    name: person.fullName,
  }))
}

/**
 * Convert CategoryBreakdown[] to AwardTypeData[] with colors for PDF legend
 */
function toAwardTypeData(data: CategoryBreakdown[]): AwardTypeData[] {
  return data.map((item) => ({
    category: item.category ?? 'Other',
    amount: item.amount,
    percentage: item.percentage,
    color:
      AWARD_TYPE_COLORS[item.category?.toLowerCase() ?? 'other'] ?? '#6b7280',
  }))
}

/**
 * Convert GeographicDistribution[] to GeoStateData[] for PDF legend (top 5)
 */
function toGeoStateData(data: GeographicDistribution[]): GeoStateData[] {
  return data
    .filter((item) => item.stateCode && item.stateName)
    .slice(0, 5)
    .map((item) => ({
      stateCode: item.stateCode!,
      stateName: item.stateName!,
      amount: item.amount,
      percentage: item.percentage,
    }))
}

export interface UseRecipientPdfExportOptions {
  recipientId: string
  /** Chart refs from context - used to capture chart images */
  chartRefs: React.RefObject<ChartRefRegistry>
}

export interface UseRecipientPdfExportReturn {
  /** Trigger PDF export */
  exportToPdf: () => Promise<void>
  /** Whether export is in progress */
  isExporting: boolean
}

/**
 * Hook for exporting recipient details to a professional PDF report.
 *
 * Uses dynamic imports to load @react-pdf/renderer only when the export is triggered,
 * keeping the main bundle small (bundle-dynamic-imports).
 *
 * Charts are captured as SVG-to-PNG (non-blocking). Legends are rendered using
 * @react-pdf components with the actual data.
 */
export function useRecipientPdfExport({
  recipientId,
  chartRefs,
}: UseRecipientPdfExportOptions): UseRecipientPdfExportReturn {
  const [isExporting, setIsExporting] = useState(false)
  const queryClient = useQueryClient()

  const exportToPdf = useCallback(async () => {
    setIsExporting(true)
    const loadingToast = toast.loading('Generating PDF report...')

    try {
      // 1. Collect all cached data from React Query
      // Profile data (dedicated query)
      const profile = queryClient.getQueryData<RecipientProfile>(
        usaspendingKeys.recipientProfile(recipientId),
      )

      // Read the batched dashboard cache (primary source for most section data)
      const dashboard = queryClient.getQueryData<RecipientDashboard>(
        recipientAnalyticsKeys.dashboard(recipientId),
      )

      // Stats: dedicated query first, dashboard fallback
      const statsRaw = queryClient.getQueryData<{
        totalObligatedAmount: number | string
        totalPrimeAwards: number | string
        totalContractAmount?: number | string
        lastTransactionDate?: string
      }>(usaspendingKeys.recipientStats(recipientId))

      // Normalize stats - amounts may come as strings
      const stats = statsRaw
        ? {
            totalObligatedAmount: Number(statsRaw.totalObligatedAmount),
            totalPrimeAwards: Number(statsRaw.totalPrimeAwards),
            totalContractAmount: statsRaw.totalContractAmount
              ? Number(statsRaw.totalContractAmount)
              : undefined,
            lastTransactionDate: statsRaw.lastTransactionDate,
          }
        : dashboard?.stats
          ? {
              totalObligatedAmount: dashboard.stats.totalObligatedAmount,
              totalPrimeAwards: dashboard.stats.totalPrimeAwards,
              totalContractAmount: undefined,
              lastTransactionDate:
                dashboard.stats.lastTransactionDate ?? undefined,
            }
          : undefined

      // Analytics data — dashboard first, individual key fallback
      const topAgencies =
        dashboard?.topAgencies ??
        queryClient.getQueryData<TopCategory[]>(
          usaspendingKeys.recipientAnalyticsAgencies(recipientId),
        ) ??
        []

      const naicsCodes =
        dashboard?.topNaics ??
        queryClient.getQueryData<TopCategory[]>(
          usaspendingKeys.recipientAnalyticsNaics(recipientId),
        ) ??
        []

      const pscCodes =
        dashboard?.topPsc ??
        queryClient.getQueryData<TopCategory[]>(
          usaspendingKeys.recipientAnalyticsPsc(recipientId),
        ) ??
        []

      const awardTypesRaw =
        dashboard?.awardTypes ??
        queryClient.getQueryData<CategoryBreakdown[]>(
          recipientAnalyticsKeys.awardTypes(recipientId),
        ) ??
        []

      const geographyRaw =
        dashboard?.geography ??
        queryClient.getQueryData<GeographicDistribution[]>(
          recipientAnalyticsKeys.geography(recipientId),
        ) ??
        []

      // Insights: dashboard first, then individual key (may be array or wrapped)
      const insightsFromIndividual = queryClient.getQueryData<
        RecipientInsight[] | { insights: RecipientInsight[] }
      >(usaspendingKeys.recipientInsights(recipientId, 4))
      const insights =
        dashboard?.insights ??
        (Array.isArray(insightsFromIndividual)
          ? insightsFromIndividual
          : insightsFromIndividual?.insights) ??
        []

      // Recent awards (dedicated query — not in dashboard)
      const recentAwardsData =
        queryClient.getQueryData<RecipientAwardSummary[]>(
          usaspendingKeys.recipientRecentAwards(recipientId, 5),
        ) ?? []

      if (!profile) {
        throw new Error('Recipient profile data not loaded')
      }

      // 2. Capture chart images in parallel (SVG-to-PNG, non-blocking) (async-parallel)
      const refs = chartRefs.current ?? {}
      const [spendingTrend, awardMix, geography] = await Promise.all([
        captureChartImage(refs.spendingTrend),
        captureChartImage(refs.awardMix),
        captureChartImage(refs.geography),
      ])

      const chartImages: ChartImages = {
        spendingTrend,
        awardMix,
        geography,
      }

      // 3. Build PDF data object
      const contractPct =
        stats?.totalContractAmount !== undefined &&
        stats?.totalObligatedAmount &&
        stats.totalObligatedAmount > 0
          ? Math.round(
              (stats.totalContractAmount / stats.totalObligatedAmount) * 100,
            )
          : undefined

      const pdfData: RecipientPdfData = {
        name: profile.name,
        location: {
          city: profile.location.city,
          state: profile.location.state,
        },
        parentName: profile.parent?.name,
        identifiers: {
          uei: profile.uei,
          duns: profile.duns,
          cageCode: profile.cageCode,
        },
        certifications: extractCertifications(profile.certifications),
        kpis: {
          totalObligatedAmount: stats?.totalObligatedAmount ?? 0,
          totalPrimeAwards: stats?.totalPrimeAwards ?? 0,
          contractPercentage: contractPct,
          lastTransactionDate: stats?.lastTransactionDate,
        },
        insights,
        awardTypes: toAwardTypeData(awardTypesRaw),
        geoStates: toGeoStateData(geographyRaw),
        topAgencies,
        naicsCodes: toIndustryCodeData(naicsCodes),
        pscCodes: toIndustryCodeData(pscCodes),
        recentAwards: recentAwardsData,
        officers: toExecutiveOfficers(profile.officers),
        chartImages,
        generatedAt: new Date(),
      }

      // 4. Dynamically import @react-pdf/renderer and generate PDF
      // This keeps the PDF library out of the main bundle (bundle-dynamic-imports)
      const [{ pdf }, { RecipientPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/components/usaspending/RecipientDetail/export/RecipientPdfDocument'),
      ])

      // Yield before heavy PDF generation
      await yieldToBrowser(50)

      // Generate PDF blob
      const blob = await pdf(<RecipientPdfDocument data={pdfData} />).toBlob()

      // 5. Download the file
      const filename = `${sanitizeFilename(pdfData.name)}-${formatDateForFilename(pdfData.generatedAt)}.pdf`
      saveAs(blob, filename)

      toast.success('PDF report exported successfully!')
    } catch (error) {
      console.error('Failed to export PDF:', error)
      toast.error(
        `Failed to export PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    } finally {
      setIsExporting(false)
      toast.dismiss(loadingToast)
    }
  }, [recipientId, queryClient, chartRefs])

  return {
    exportToPdf,
    isExporting,
  }
}
